import { useState, useId } from 'react';
import styles from './JobsList.module.scss';
import classNames from 'classnames';
import { UI_Button } from 'ui/components/1-atoms/Action/Button/Button';

export interface JobsListFieldOfWork {
	id: string;
	description: string;
}

export interface JobsListLocation {
	id: string;
	description: string;
}

export interface JobsListItem {
	id: string;
	description: string;
	detailsUrl: string;
	endDate: string;
	fieldsOfWork: JobsListFieldOfWork[];
	locations: JobsListLocation[];
}

export interface JobsListTexts {
	titleColumnHeader: string;
	locationColumnHeader: string;
	deadlineColumnHeader: string;
	loadMoreButtonText: string;
	showingCount: (visible: number, total: number) => string;
	noResultsText: string;
}

export interface JobsListProps {
	className?: string;
	items: JobsListItem[];
	texts: JobsListTexts;
	pageSize?: number;
}

export const UI_JobsList = ({ className, items, texts, pageSize = 10 }: JobsListProps) => {
	const [visibleCount, setVisibleCount] = useState(pageSize);
	const [trackedLength, setTrackedLength] = useState(items.length);
	const [trackedPageSize, setTrackedPageSize] = useState(pageSize);

	if (trackedLength !== items.length || trackedPageSize !== pageSize) {
		setTrackedLength(items.length);
		setTrackedPageSize(pageSize);
		setVisibleCount(pageSize);
	}
	const id = useId();
	const titleId = `${id}-title`;
	const locationId = `${id}-location`;
	const deadlineId = `${id}-deadline`;

	const visibleItems = items.slice(0, visibleCount);
	const hasMore = visibleCount < items.length;

	return (
		<div className={classNames(styles.JobsList, className)}>
			<div className={styles.JobsList_header}>
				<span id={titleId} className={styles.JobsList_headerCell}>
					{texts.titleColumnHeader}
				</span>
				<span id={locationId} className={styles.JobsList_headerCell}>
					{texts.locationColumnHeader}
				</span>
				<span id={deadlineId} className={styles.JobsList_headerCell}>
					{texts.deadlineColumnHeader}
				</span>
			</div>
			{items.length === 0 ? (
				<p className={styles.JobsList_noResults}>{texts.noResultsText}</p>
			) : (
				<>
					<ul className={styles.JobsList_list}>
						{visibleItems.map((item) => (
							<li key={item.id} className={styles.JobsList_item}>
								<a href={item.detailsUrl} className={styles.JobsList_row}>
									<span className={styles.JobsList_cell} aria-describedby={titleId} data-css-label={texts.titleColumnHeader}>
										{item.description}
									</span>
									<span className={styles.JobsList_cell} aria-describedby={locationId} data-css-label={texts.locationColumnHeader}>
										{item.locations.map((l) => l.description).join(', ')}
									</span>
									<span
										className={classNames(styles.JobsList_cell, styles.JobsList_cell___deadline)}
										aria-describedby={deadlineId}
										data-css-label={texts.deadlineColumnHeader}
									>
										{item.endDate}
									</span>
								</a>
							</li>
						))}
					</ul>
					<div className={styles.JobsList_pagination}>
						<span className={styles.JobsList_showingCount}>{texts.showingCount(visibleItems.length, items.length)}</span>
						{hasMore && (
							<UI_Button style="secondary" size="small" onClick={() => setVisibleCount((prev) => prev + pageSize)}>
								{texts.loadMoreButtonText}
							</UI_Button>
						)}
					</div>
				</>
			)}
		</div>
	);
};
